import React, { createContext, useContext, useCallback, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Strips trailing /forms/drawer/:id or /forms/full/:id segments (repeatedly) so the
 * module base path (e.g. /companies) is returned. Without this, paths like
 * /companies/forms/full/:id are treated as the "base" and opening a drawer appends
 * another /forms/drawer/:id and corrupts the URL.
 */
/** Strips trailing `/forms/drawer/:id` and `/forms/full/:id` segments (repeatedly). Used when closing full form after delete so we do not reopen a drawer URL still stored on the module tab. */
export function stripTrailingFormRouteSegments(pathname) {
  let path = pathname
  for (;;) {
    const drawer = path.match(/^(.+)\/forms\/drawer\/[^/]+$/)
    if (drawer) {
      path = drawer[1]
      continue
    }
    const full = path.match(/^(.+)\/forms\/full\/[^/]+$/)
    if (full) {
      path = full[1]
      continue
    }
    break
  }
  return path
}

/**
 * The value DrawerProvider publishes (and an overriding provider must supply).
 *
 * @typedef {Object} DrawerContextValue
 * @property {boolean} isOpen
 * @property {string | null} basePath
 * @property {string | null} recordId
 * @property {boolean} isNew
 * @property {string | null} formType
 * @property {{ recordId: string | number, basePath: string } | null} pendingNavigation
 * @property {(formType?: string) => void} openDrawerForNew
 * @property {(recordId: string | number) => void} openDrawerForRecord
 * @property {() => void} closeDrawer
 * @property {(recordId: string | number) => void} updateDrawerRecord
 * @property {() => void} confirmPendingNavigation
 * @property {() => void} cancelPendingNavigation
 * @property {() => string} getCurrentBasePath
 */

// Create the context. Exported so the global quick-add drawer can wrap the
// same DrawerNew forms in an overridden provider (isNew/isOpen true, actions
// closing the overlay) without a /forms/drawer/ URL.
/** @type {React.Context<DrawerContextValue | undefined>} */
export const DrawerContext = createContext()

/**
 * Custom hook to access drawer functionality
 * Provides methods to open, close, and manage drawer state
 */
export function useDrawer() {
  const context = useContext(DrawerContext)
  if (!context) {
    throw new Error('useDrawer must be used within a DrawerProvider')
  }
  return context
}

/**
 * DrawerProvider component that manages drawer state through URL routing
 * Supports patterns like: /deals/forms/drawer/123 or /deals/forms/drawer/new
 */
export function DrawerProvider({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [pendingNavigation, setPendingNavigation] = useState(null) // { recordId, basePath }

  // Module table base path, regardless of whether a full form or drawer segment is active
  const getCurrentBasePath = useCallback(() => {
    return stripTrailingFormRouteSegments(location.pathname)
  }, [location.pathname])

  // Extract form type from base path (e.g., '/deals' -> 'deal')
  const getFormTypeFromPath = useCallback((basePath) => {
    // Special case for pipeline paths (same last segment, different form types)
    if (basePath === '/fundraising/pipeline') return 'prospect'
    if (basePath === '/deals/pipeline') return 'deal'

    const segments = basePath.split('/').filter(Boolean)
    const lastSegment = segments[segments.length - 1]

    // Convert plural to singular for form types
    const pluralToSingular = {
      deals: 'deal',
      companies: 'company',
      contacts: 'contact',
      operations: 'operation',
      lookups: 'lookup',
      rounds: 'round',
      fundraising: 'prospect',
      prospects: 'prospect'
    }

    return pluralToSingular[lastSegment] || lastSegment
  }, [])

  // Parse drawer state from current URL (match rightmost /forms/drawer/:id only)
  const getDrawerState = useCallback(() => {
    const path = location.pathname
    const drawerSuffix = /\/forms\/drawer\/([^/]+)$/
    const match = path.match(drawerSuffix)

    if (match) {
      const recordSegment = match[1]
      const baseWithoutDrawer = path.slice(0, path.length - match[0].length)
      const basePath = stripTrailingFormRouteSegments(baseWithoutDrawer)
      return {
        isOpen: true,
        basePath,
        recordId: recordSegment === 'new' ? null : recordSegment,
        isNew: recordSegment === 'new',
        formType: getFormTypeFromPath(basePath)
      }
    }

    return {
      isOpen: false,
      basePath: null,
      recordId: null,
      isNew: false,
      formType: null
    }
  }, [location.pathname, getFormTypeFromPath])

  // Open drawer for new record
  const openDrawerForNew = useCallback(
    (formType) => {
      const currentBasePath = getCurrentBasePath()
      navigate(`${currentBasePath}/forms/drawer/new`)
    },
    [navigate, getCurrentBasePath]
  )

  // Open drawer for existing record
  const openDrawerForRecord = useCallback(
    (recordId) => {
      const drawerState = getDrawerState()
      // If currently viewing a new record, show confirmation instead of navigating immediately
      if (drawerState.isNew) {
        const currentBasePath = getCurrentBasePath()
        setPendingNavigation({ recordId, basePath: currentBasePath })
        return
      }
      // Otherwise, navigate immediately
      const currentBasePath = getCurrentBasePath()
      navigate(`${currentBasePath}/forms/drawer/${recordId}`)
    },
    [navigate, getCurrentBasePath, getDrawerState]
  )

  // Confirm pending navigation
  const confirmPendingNavigation = useCallback(() => {
    if (pendingNavigation) {
      const { recordId, basePath } = pendingNavigation
      setPendingNavigation(null)
      navigate(`${basePath}/forms/drawer/${recordId}`)
    }
  }, [navigate, pendingNavigation])

  // Cancel pending navigation
  const cancelPendingNavigation = useCallback(() => {
    setPendingNavigation(null)
  }, [])

  // Close drawer and return to base path
  const closeDrawer = useCallback(() => {
    const drawerState = getDrawerState()
    if (drawerState.isOpen && drawerState.basePath) {
      navigate(drawerState.basePath)
    }
  }, [navigate, getDrawerState])

  // Update drawer with new record ID (useful after creating a new record)
  const updateDrawerRecord = useCallback(
    (recordId) => {
      const drawerState = getDrawerState()
      if (drawerState.isOpen && drawerState.basePath) {
        navigate(`${drawerState.basePath}/forms/drawer/${recordId}`, { replace: true })
      }
    },
    [navigate, getDrawerState]
  )

  const drawerState = getDrawerState()

  // Clear pending navigation when drawer closes or when isNew becomes false
  useEffect(() => {
    if (!drawerState.isOpen || !drawerState.isNew) {
      setPendingNavigation(null)
    }
  }, [drawerState.isOpen, drawerState.isNew])

  const contextValue = {
    // State
    ...drawerState,
    pendingNavigation,

    // Actions
    openDrawerForNew,
    openDrawerForRecord,
    closeDrawer,
    updateDrawerRecord,
    confirmPendingNavigation,
    cancelPendingNavigation,

    // Helpers
    getCurrentBasePath
  }

  return <DrawerContext.Provider value={contextValue}>{children}</DrawerContext.Provider>
}
